"use client";

import type React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import type {
  EventInput,
  EventClickArg,
  DatesSetArg,
} from "@fullcalendar/core";
import {
  format,
  isBefore,
  startOfDay,
  addHours,
  parseISO,
  isValid,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Truck,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { DeveliryCalendarOrderDetailsModal } from "./components/delivery/DeliveryCalendarOrderDetailsModal";
import {
  type CalendarOutput,
  OrderDeliveryType,
  OrderStatus,
} from "@/interfaces";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { CalendarService } from "@/services/CalendarService";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const DeliveryCalendar: React.FC = () => {
  const [selectedCalendar, setSelectedCalendar] =
    useState<CalendarOutput | null>(null);
  const [calendars, setCalendars] = useState<CalendarOutput[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const [currentMonthDisplay, setCurrentMonthDisplay] = useState<string>(
    format(new Date(), "MMMM yyyy", { locale: ptBR })
  );

  // Referência para o calendário
  const calendarRef = useRef<FullCalendar | null>(null);
  const service = new CalendarService();

  // Referência para controlar requisições
  const fetchingRef = useRef<boolean>(false);
  const lastFetchedMonthRef = useRef<string>("");

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch deliveries for a specific month
  const fetchDeliveries = useCallback(async (monthYear: string) => {
    // Verificar se já estamos buscando ou se já buscamos este mês
    if (fetchingRef.current || lastFetchedMonthRef.current === monthYear) {
      return;
    }

    // Marcar que estamos buscando
    fetchingRef.current = true;
    setLoading(true);

    try {
      const { data } = await service.findAll({
        expand: ["customers"],
        monthYear,
      });

      // Atualizar o último mês buscado
      lastFetchedMonthRef.current = monthYear;

      // Atualizar os dados do calendário
      setCalendars(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao buscar dados do calendário:", error);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  // Carregar dados iniciais
  useEffect(() => {
    // Buscar dados para o mês atual na montagem inicial
    if (lastFetchedMonthRef.current === "") {
      const today = new Date();
      const currentMonthYear = format(today, "MM/yyyy");
      fetchDeliveries(currentMonthYear);
      setCurrentMonthDisplay(format(today, "MMMM yyyy", { locale: ptBR }));
    }
  }, [fetchDeliveries]);

  // Handle date range change in calendar
  const handleDatesSet = (dateInfo: DatesSetArg) => {
    // Verificar se a visualização é mensal
    if (dateInfo.view.type === "dayGridMonth") {
      // Importante: usar a data central do calendário, não a data de início do intervalo
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        const centralDate = calendarApi.getDate(); // Esta é a data central do calendário

        // Formatar para exibição e para busca
        const formattedMonthDisplay = format(centralDate, "MMMM yyyy", {
          locale: ptBR,
        });
        const monthYear = format(centralDate, "MM/yyyy");

        // Atualizar a exibição do mês
        setCurrentMonthDisplay(formattedMonthDisplay);

        // Verificar se já buscamos este mês
        if (lastFetchedMonthRef.current !== monthYear) {
          fetchDeliveries(monthYear);
        }
      }
    }
  };

  // Função para processar uma data de string para objeto Date
  const processDate = (dateString: string | Date | undefined | null): Date => {
    // Se for undefined ou null, retornar a data atual
    if (!dateString) {
      return new Date();
    }

    // Se já for um objeto Date, retornar diretamente
    if (dateString instanceof Date) {
      return dateString;
    }

    try {
      // Tentar fazer o parse da data
      const parsedDate = parseISO(dateString);

      // Verificar se a data é válida
      if (isValid(parsedDate)) {
        return parsedDate;
      }
    } catch (error) {
      console.error("Erro ao processar data:", error, dateString);
    }

    // Fallback para a data atual se houver erro
    return new Date();
  };

  // Map calendar data to FullCalendar events
  const events: EventInput[] = calendars.map((calendar) => {
    try {
      // Determinar a data de início
      const rawDate =
        calendar.orderDeliveryType === OrderDeliveryType.DELIVERY
          ? calendar.orderDeliveryAt || calendar.orderDate
          : calendar.orderDate;

      const dateObj = processDate(rawDate);

      const eventDate = startOfDay(dateObj);
      const startDate = addHours(eventDate, 12);
      const endDate = addHours(startDate, 0.5);

      let originalTimeDisplay = "";
      try {
        originalTimeDisplay = format(dateObj, "HH:mm");
      } catch {
        originalTimeDisplay = "00:00"; // Fallback se não conseguir formatar
      }

      return {
        id: calendar.orderId,
        title: calendar.customerName || calendar.orderId,
        start: startDate,
        end: endDate,
        allDay: false, // Importante: definir como false para evitar que ocupe dois dias
        display: "block", // Exibir como bloco para ocupar menos espaço
        extendedProps: {
          orderId: calendar.orderId,
          customerName: calendar.customerName,
          address: calendar.address ? calendar.address : "",
          status: calendar.orderStatus,
          deliveryType: calendar.orderDeliveryType,
          isLate: orderIsLate(calendar),
          originalDate: dateObj, // Manter a data original para exibição no tooltip
          originalTimeDisplay, // Horário formatado para exibição
        },
        backgroundColor: getStatusColor(calendar),
        borderColor: getStatusColor(calendar),
        textColor: "#ffffff",
      };
    } catch (error) {
      console.error("Erro ao processar evento do calendário:", error, calendar);

      // Retornar um evento com dados mínimos em caso de erro
      return {
        id: calendar.orderId || "unknown",
        title:
          (calendar.customerName || calendar.orderId || "Pedido") + " (erro)",
        start: new Date(), // Usar a data atual como fallback
        backgroundColor: "#6c757d", // Cinza para indicar erro
        extendedProps: {
          error: true,
          orderId: calendar.orderId,
          deliveryType: OrderDeliveryType.WITHDRAWAL,
          isLate: false,
          originalTimeDisplay: "00:00",
        },
      };
    }
  });

  // Check if order is late
  function orderIsLate(calendar: CalendarOutput): boolean {
    try {
      return (
        calendar.orderStatus !== OrderStatus.DONE &&
        ((calendar.orderDeliveryType === OrderDeliveryType.DELIVERY &&
          calendar.orderDeliveryAt &&
          isBefore(processDate(calendar.orderDeliveryAt), new Date())) ||
          (calendar.orderDeliveryType === OrderDeliveryType.WITHDRAWAL &&
            isBefore(processDate(calendar.orderDate), new Date())))
      );
    } catch (error) {
      console.error("Erro ao verificar se pedido está atrasado:", error);
      return false;
    }
  }

  // Get color based on order status
  function getStatusColor(calendar: CalendarOutput): string {
    try {
      if (orderIsLate(calendar)) {
        return "#dc3545"; // Red for late orders
      }

      switch (calendar.orderStatus) {
        case OrderStatus.PENDING:
          return "#3788d8"; // Blue for pending
        case OrderStatus.SCHEDULED:
          return "#3b82f6"; // Blue for scheduled
        case OrderStatus.IN_PREPARATION:
          return "#f59e0b"; // Amber for in preparation
        case OrderStatus.PREPARED:
          return "#14b8a6"; // Teal for prepared
        case OrderStatus.DONE:
          return "#28a745"; // Green for completed
        case OrderStatus.CANCELED:
          return "#6c757d"; // Gray for canceled
        default:
          return "#f59e0b"; // Amber for other statuses
      }
    } catch (error) {
      console.error("Erro ao determinar cor do status:", error);
      return "#6c757d"; // Cinza como fallback em caso de erro
    }
  }

  // Handle event click to show details
  const handleEventClick = (clickInfo: EventClickArg) => {
    const delivery = calendars.find((d) => d.orderId === clickInfo.event.id);
    if (delivery) {
      setSelectedCalendar(delivery);
    }
  };

  // Determine calendar view based on screen size
  const getCalendarView = useCallback(() => {
    if (windowWidth < 768) {
      return "listWeek";
    } else if (windowWidth < 1024) {
      return "dayGridWeek";
    } else {
      return "dayGridMonth";
    }
  }, [windowWidth]);

  // Update calendar view when screen size changes
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(getCalendarView());
    }
  }, [windowWidth, getCalendarView]);

  // Navegar para o mês anterior
  const goToPrevMonth = useCallback(() => {
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.prev();

      // Após a navegação, obter a data central e buscar dados
      setTimeout(() => {
        const centralDate = api.getDate();
        const monthYear = format(centralDate, "MM/yyyy");
        setCurrentMonthDisplay(
          format(centralDate, "MMMM yyyy", { locale: ptBR })
        );

        if (lastFetchedMonthRef.current !== monthYear) {
          fetchDeliveries(monthYear);
        }
      }, 100); // Pequeno atraso para garantir que a navegação foi concluída
    }
  }, [fetchDeliveries]);

  // Navegar para o mês seguinte
  const goToNextMonth = useCallback(() => {
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.next();

      // Após a navegação, obter a data central e buscar dados
      setTimeout(() => {
        const centralDate = api.getDate();
        const monthYear = format(centralDate, "MM/yyyy");
        setCurrentMonthDisplay(
          format(centralDate, "MMMM yyyy", { locale: ptBR })
        );

        if (lastFetchedMonthRef.current !== monthYear) {
          fetchDeliveries(monthYear);
        }
      }, 100); // Pequeno atraso para garantir que a navegação foi concluída
    }
  }, [fetchDeliveries]);

  // Navegar para hoje
  const goToToday = useCallback(() => {
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.today();

      // Após a navegação, obter a data central e buscar dados
      setTimeout(() => {
        const centralDate = api.getDate();
        const monthYear = format(centralDate, "MM/yyyy");
        setCurrentMonthDisplay(
          format(centralDate, "MMMM yyyy", { locale: ptBR })
        );

        if (lastFetchedMonthRef.current !== monthYear) {
          fetchDeliveries(monthYear);
        }
      }, 100); // Pequeno atraso para garantir que a navegação foi concluída
    }
  }, [fetchDeliveries]);

  return (
    <div className="flex flex-col bg-white rounded-lg shadow-sm h-[calc(100vh-80px)] overflow-hidden">
      <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-blue-100">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            <h1 className="text-xl font-bold text-gray-800">
              Calendário de Entregas
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={goToPrevMonth}
              className="border-blue-200 hover:bg-blue-50"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Anterior</span>
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={goToToday}
              className="border-blue-200 hover:bg-blue-50 font-medium"
            >
              <Calendar className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Hoje</span>
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={goToNextMonth}
              className="border-blue-200 hover:bg-blue-50"
            >
              <span className="hidden sm:inline">Próximo</span>
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium text-gray-600 capitalize">
            {currentMonthDisplay}
          </div>

          <div className="flex flex-wrap gap-2">
            <StatusLegend color="#3788d8" label="Pendente" />
            <StatusLegend color="#3b82f6" label="Agendado" />
            <StatusLegend color="#f59e0b" label="Em preparação" />
            <StatusLegend color="#14b8a6" label="Pronto" />
            <StatusLegend color="#28a745" label="Concluído" />
            <StatusLegend color="#dc3545" label="Atrasado" />
            <StatusLegend color="#6c757d" label="Cancelado" />
          </div>
        </div>
      </div>

      <div className="flex-grow overflow-auto p-2 sm:p-4">
        {loading && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
            <LoadingSpinner />
          </div>
        )}

        <Card className="border shadow-sm">
          <CardContent className="p-0">
            <FullCalendar
              ref={calendarRef}
              plugins={[
                dayGridPlugin,
                timeGridPlugin,
                interactionPlugin,
                listPlugin,
              ]}
              initialView={getCalendarView()}
              headerToolbar={{
                left: "title",
                center: "",
                right:
                  windowWidth >= 640
                    ? "dayGridMonth,timeGridWeek,timeGridDay,listWeek"
                    : "",
              }}
              buttonText={{
                today: "Hoje",
                month: "Mês",
                week: "Semana",
                day: "Dia",
                list: "Lista",
              }}
              views={{
                listWeek: {
                  type: "list",
                  duration: { days: 7 },
                  buttonText: "Lista",
                },
              }}
              locale={ptBR}
              events={events}
              eventClick={handleEventClick}
              editable={false}
              eventContent={renderEventContent}
              height="auto"
              contentHeight="auto"
              aspectRatio={1.35}
              stickyHeaderDates={true}
              datesSet={handleDatesSet}
              dayMaxEvents={3}
              eventTimeFormat={{
                hour: "2-digit",
                minute: "2-digit",
                meridiem: false,
              }}
              nextDayThreshold="00:00:00" // Define quando um evento passa para o próximo dia
              forceEventDuration={true} // Força a duração do evento
            />
          </CardContent>
        </Card>
      </div>

      {selectedCalendar && (
        <DeveliryCalendarOrderDetailsModal
          order={selectedCalendar}
          onClose={() => setSelectedCalendar(null)}
        />
      )}
    </div>
  );
};

// Status legend component
const StatusLegend: React.FC<{ color: string; label: string }> = ({
  color,
  label,
}) => (
  <div className="flex items-center gap-1.5">
    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
    <span className="text-xs text-gray-600">{label}</span>
  </div>
);

// Event content renderer with proper typing
function renderEventContent(eventInfo: {
  event: {
    title: string;
    start: Date;
    extendedProps: {
      deliveryType: OrderDeliveryType;
      isLate: boolean;
      address?: string;
      originalDate?: Date;
      originalTimeDisplay: string;
      error?: boolean;
    };
  };
  timeText: string;
  view: {
    type: string;
  };
}) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const isTablet =
    typeof window !== "undefined" &&
    window.innerWidth >= 768 &&
    window.innerWidth < 1024;
  const isListView = eventInfo.view.type.includes("list");
  const hasError = eventInfo.event.extendedProps.error === true;

  // For list view, we use a different layout
  if (isListView) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2 p-1">
              {hasError ? (
                <AlertTriangle className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
              ) : (
                <>
                  {eventInfo.event.extendedProps.isLate && (
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                  )}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {eventInfo.event.extendedProps.deliveryType ===
                    OrderDeliveryType.DELIVERY ? (
                      <Truck className="h-3.5 w-3.5 text-gray-500" />
                    ) : (
                      <Clock className="h-3.5 w-3.5 text-gray-500" />
                    )}
                    <span className="text-xs font-medium">
                      {eventInfo.event.extendedProps.originalTimeDisplay}
                    </span>
                  </div>
                </>
              )}
              <div className="font-medium truncate">
                {eventInfo.event.title}
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-1 text-xs">
              <p className="font-bold">{eventInfo.event.title}</p>
              {!hasError && eventInfo.event.extendedProps.originalDate && (
                <p>
                  {format(
                    eventInfo.event.extendedProps.originalDate,
                    "dd/MM/yyyy HH:mm"
                  )}
                </p>
              )}
              {eventInfo.event.extendedProps.address && (
                <p className="text-gray-500">
                  {eventInfo.event.extendedProps.address}
                </p>
              )}
              {eventInfo.event.extendedProps.isLate && (
                <Badge variant="destructive" className="mt-1">
                  Atrasado
                </Badge>
              )}
              {hasError && (
                <Badge
                  variant="outline"
                  className="mt-1 border-gray-300 text-gray-500"
                >
                  Erro ao carregar detalhes
                </Badge>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // For mobile view
  if (isMobile) {
    return (
      <div className="fc-event-main-frame p-1">
        {hasError ? (
          <AlertTriangle className="h-3 w-3 text-white absolute top-0.5 right-0.5" />
        ) : (
          eventInfo.event.extendedProps.isLate && (
            <AlertTriangle className="h-3 w-3 text-white absolute top-0.5 right-0.5" />
          )
        )}
        <div className="fc-event-title fc-sticky text-xs font-semibold truncate">
          {eventInfo.event.title}
        </div>
      </div>
    );
  }

  // For tablet view
  if (isTablet) {
    return (
      <div className="fc-event-main-frame p-1">
        <div className="flex items-center gap-1">
          <div className="fc-event-time text-xs flex items-center">
            {hasError ? (
              <AlertTriangle className="h-3 w-3 mr-0.5" />
            ) : (
              <>
                {eventInfo.event.extendedProps.deliveryType ===
                OrderDeliveryType.DELIVERY ? (
                  <Truck className="h-3 w-3 mr-0.5" />
                ) : (
                  <Clock className="h-3 w-3 mr-0.5" />
                )}
              </>
            )}
            {eventInfo.event.extendedProps.originalTimeDisplay}
          </div>
          {!hasError && eventInfo.event.extendedProps.isLate && (
            <AlertTriangle className="h-3 w-3" />
          )}
        </div>
        <div className="fc-event-title fc-sticky text-xs font-semibold truncate">
          {eventInfo.event.title}
        </div>
      </div>
    );
  }

  // For desktop view
  return (
    <div className="fc-event-main-frame p-1">
      <div className="flex items-center justify-between">
        <div className="fc-event-time text-xs flex items-center">
          {hasError ? (
            <AlertTriangle className="h-3 w-3 mr-0.5" />
          ) : (
            <>
              {eventInfo.event.extendedProps.deliveryType ===
              OrderDeliveryType.DELIVERY ? (
                <Truck className="h-3 w-3 mr-0.5" />
              ) : (
                <Clock className="h-3 w-3 mr-0.5" />
              )}
            </>
          )}
          {eventInfo.event.extendedProps.originalTimeDisplay}
        </div>
        {!hasError && eventInfo.event.extendedProps.isLate && (
          <AlertTriangle className="h-3 w-3" />
        )}
      </div>
      <div className="fc-event-title-container">
        <div className="fc-event-title fc-sticky text-xs sm:text-sm font-semibold truncate">
          {eventInfo.event.title}
        </div>
      </div>
    </div>
  );
}
